import React, { useState } from 'react';
import Waveform from './Waveform';
import TestWaveform from './TestWaveform';
import SimpleAudio from './SimpleAudio';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Divider, 
  LinearProgress,
  useTheme,
  Chip,
  Collapse,
  Grid
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import PauseIcon from '@mui/icons-material/Pause';

// Animationer
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(66, 133, 244, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(66, 133, 244, 0);
  }
`;

// Waveform bruger ekstern komponent, så ingen lokal styling nødvendig

// Definerer typer til segmenter
interface Segment {
  name: string;
  score: number;
  color: string;
}

// Props interface
interface CallCardProps {
  id: number | string;
  customer: string;  // Nu bruges dette felt til at vise opkalds-ID i stedet for kundenavn
  date: string;
  time: string;
  duration: string;
  talkRatio: number;
  authority: number;
  toneVariation: number;
  energyVariation: number;
  segments: Segment[];
  actionPoints?: {
    toneVariation?: string;
    talkTime?: string;
  };
  audioUrl?: string; // ny valgfri
  playing?: boolean; // ny valgfri for at kontrollere afspilning
  onPlayClick?: (id: number | string) => void;
  onExpandClick?: (id: number | string) => void;
}

// Styled components
const CardContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '10px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
  },
}));

const ScoreChip = styled(Chip)<{ score: number }>(({ theme, score }) => {
  let backgroundColor = '#64b5f6'; // Default blue
  
  if (score >= 80) {
    backgroundColor = '#4caf50'; // Green for high scores
  } else if (score >= 60) {
    backgroundColor = '#ff9800'; // Orange for medium scores
  } else {
    backgroundColor = '#f44336'; // Red for low scores
  }
  
  return {
    backgroundColor,
    color: 'white',
    fontWeight: 'bold',
    borderRadius: '4px',
    height: '24px',
  };
});

const ProgressBarContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SegmentProgress = styled(LinearProgress)<{ barcolor: string }>(({ theme, barcolor }) => ({
  height: 6,
  borderRadius: 3,
  flex: 1,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    backgroundColor: barcolor,
  },
}));

const StyledPlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  width: 36,
  height: 36,
  marginLeft: theme.spacing(1.5),
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.palette.text.secondary,
}));

const TalkRatioBar = styled(Box)<{ ratio: number }>(({ theme, ratio }) => ({
  display: 'flex',
  height: '8px',
  width: '100%',
  borderRadius: '4px',
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[200],
  marginTop: theme.spacing(2),
  '& .agent-talk': {
    width: `${(ratio * 100).toFixed(1)}%`,
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease-in-out'
  },
  '& .customer-talk': {
    width: `${((1 - ratio) * 100).toFixed(1)}%`,
    backgroundColor: theme.palette.secondary.main,
    transition: 'width 0.3s ease-in-out'
  },
}));

const TalkRatioText = styled(Typography)({
  fontSize: '0.68rem',
  color: 'text.secondary',
  marginTop: '3px'
});

const CallCard: React.FC<CallCardProps> = ({
  id,
  customer,
  date,
  time,
  duration,
  talkRatio,
  authority,
  toneVariation,
  energyVariation,
  segments,
  actionPoints,
  audioUrl,
  playing = false,
  onPlayClick,
  onExpandClick
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  
  // Calculate average score from segments
  const avgScore = Math.round(
    segments.reduce((acc, segment) => acc + segment.score, 0) / segments.length
  );
  
  const handlePlay = () => {
    if (onPlayClick) onPlayClick(id);
  };
  
  const handleExpand = () => {
    setExpanded(!expanded);
    if (onExpandClick) onExpandClick(id);
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString('da-DK', options);
  };

  return (
    <CardContainer>
      {/* Sammenfoldede visning */}
      <Grid container spacing={2}>
        {/* Venstre side med kundenavn og dato */}
        <Grid item xs={7} sm={4} md={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 0 }}>
                {customer}
              </Typography>
              <Box sx={{
                animation: playing ? `${pulse} 2s infinite` : 'none',
                borderRadius: '50%',
                display: 'inline-flex',
                ml: 1.5,
              }}>
                <StyledPlayButton onClick={handlePlay} size="small">
                  {playing ? <PauseIcon /> : <PlayArrowIcon />}
                </StyledPlayButton>
              </Box>
              {/* Simpel audio afspilning */}
              <Box sx={{ flexGrow: 1, ml: 2 }}>
                <SimpleAudio url={audioUrl} playing={playing} />
              </Box>
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              {formatDate(date)} • {time}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">{duration}</Typography>
            </Box>
          </Box>
        </Grid>
        
        {/* Midtersektion - Segmenter og score */}
        <Grid item xs={5} sm={5} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
            {/* Segmentgrafikken er fjernet efter ønske – efterlader blot hvid plads */}
            
            {/* Talk ratio bar */}
            <TalkRatioBar ratio={talkRatio}>
              <Box className="agent-talk" />
              <Box className="customer-talk" />
            </TalkRatioBar>
            
            <TalkRatioText variant="caption" color="textSecondary">
              Agent vs. Kunde taletid ({(talkRatio * 100).toFixed(1)}% / {((1 - talkRatio) * 100).toFixed(1)}%)
            </TalkRatioText>
          </Box>
        </Grid>
        
        {/* Højre side - Score og Expand knap */}
        <Grid item xs={12} sm={3} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ mr: 2 }}>
              <ScoreChip label={avgScore} score={avgScore} size="small" />
            </Box>
            <ExpandButton onClick={handleExpand}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ExpandButton>
          </Box>
        </Grid>
      </Grid>
      
      {/* Udvidet visning */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          {/* Voice analysis */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Stemmeanalyse</Typography>
            
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <BarChartIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{authority}</Typography>
                  <Typography variant="caption" color="textSecondary">Autoritet</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <EmojiEmotionsIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{toneVariation}</Typography>
                  <Typography variant="caption" color="textSecondary">Tonevariationer</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <SpeedIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{energyVariation}</Typography>
                  <Typography variant="caption" color="textSecondary">Energi</Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Segmentanalyse</Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {segments.map((segment, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{segment.name}</Typography>
                    <ScoreChip label={segment.score} score={segment.score} size="small" />
                  </Box>
                  <ProgressBarContainer>
                    <SegmentProgress
                      variant="determinate"
                      value={segment.score}
                      barcolor={segment.color}
                    />
                  </ProgressBarContainer>
                </Box>
              ))}
            </Box>
          </Grid>
          
          {/* Improvement areas */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <TrendingUpIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
              Forbedringsområder
            </Typography>
            
            {actionPoints?.toneVariation && (
              <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', p: 1.5, borderRadius: '8px', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Tonevariation
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                  {actionPoints.toneVariation}
                </Typography>
              </Box>
            )}
            
            {actionPoints?.talkTime && (
              <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', p: 1.5, borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Taletid
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                  {actionPoints.talkTime}
                </Typography>
              </Box>
            )}
            
            {(!actionPoints?.toneVariation && !actionPoints?.talkTime) && (
              <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', p: 1.5, borderRadius: '8px' }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                  Ingen forbedringsforslag tilgængelige for dette opkald.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Collapse>
    </CardContainer>
  );
};

export default CallCard;